// src/components/admin/Meetings.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Meetings
//  "TelcoFair Glass Workspace" — same system as Companies.jsx / Dashboard.jsx
//  Layout is pure CSS Grid with the same design tokens and primitives
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Avatar,
    IconButton,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Skeleton,
    Tabs,
    Tab,
    CircularProgress,
    Fade,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Divider,
    Alert,
    Badge,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Event as EventIcon,
    Videocam as VideocamIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Schedule as ScheduleIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    FilterAltOff as FilterOffIcon,
    Download as DownloadIcon,
    CalendarToday as CalendarIcon,
    LocationOn as LocationIcon,
    Link as LinkIcon,
    Description as DescriptionIcon,
    Business as BusinessIcon,
    Chat as ChatIcon,
    Psychology as PsychologyIcon,
    Recommend as ThumbUpIcon,
    Cancel as ThumbDownIcon,
    Pending as HourglassIcon,
    Pending as PendingIcon,
    Verified as VerifiedIcon,
    Stars as StarsIcon,
    Send as SendIcon,
    FiberManualRecord as LiveIcon,
    AutoAwesome as AutoAwesomeIcon,
    Handshake as HandshakeIcon,
    RocketLaunch as RocketIcon,
    Timer as TimerIcon,
    Warning as WarningIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    deleteDoc,
    setDoc,
    serverTimestamp,
    query,
    where,
    orderBy,
    increment,
    arrayUnion,
    limit,
} from 'firebase/firestore';
import toast from 'react-hot-toast';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS (identical to Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkFaint: '#AAB6C1',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS (from Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatDate = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    const now = new Date();
    const diff = now - d;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

const LIFECYCLE_META = {
    pending: { label: 'Pending', tone: palette.warning },
    accepted: { label: 'Accepted', tone: palette.success },
    rejected: { label: 'Rejected', tone: palette.danger },
    clarification: { label: 'Clarification', tone: palette.indigo },
    scheduled: { label: 'Scheduled', tone: palette.rust },
    completed: { label: 'Completed', tone: palette.success },
    cancelled: { label: 'Cancelled', tone: palette.danger },
    confirmed: { label: 'Confirmed', tone: palette.pine },
};

const MEETING_TYPE_META = {
    video: { label: 'Video Call', tone: palette.rust },
    in_person: { label: 'In Person', tone: palette.plum },
    phone: { label: 'Phone Call', tone: palette.teal },
    hybrid: { label: 'Hybrid', tone: palette.pine },
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID PRIMITIVES (from Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const glassField = (accent = palette.rust) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    ...(lift && {
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Box sx={{ width: dense ? 34 : 44, height: 3, mt: 0.75, borderRadius: 3, background: `linear-gradient(90deg, ${accent}, ${rgba(accent, 0)})` }} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const displayValue = typeof value === 'number' ? compact(value) : value;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%', p: { xs: 2, md: 2.4 },
                    display: 'flex', flexDirection: 'column', minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: delta.trend === 'up' ? palette.success : palette.danger,
                                    bgcolor: rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.09),
                                    border: `1px solid ${rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.18)}`,
                                    '& .MuiChip-icon': { color: delta.trend === 'up' ? palette.success : palette.danger, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    {hint && (
                        <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="span"
                                sx={{
                                    width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                    boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                    animation: 'tfPulse 2.4s ease-in-out infinite',
                                }}
                            />
                            {hint}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

const MetaPill = ({ label, tone = palette.inkLight, pulse = false, icon }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.55,
            px: 1, py: 0.35, borderRadius: '9px', whiteSpace: 'nowrap',
            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.2)}`,
        }}
    >
        {icon
            ? React.cloneElement(icon, { sx: { fontSize: 12 } })
            : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone, animation: pulse ? 'tfPulse 2.4s ease-in-out infinite' : 'none' }} />}
        {label}
    </Box>
);

const StatusPill = ({ status }) => {
    const meta = LIFECYCLE_META[status] || { label: status || 'Unknown', tone: palette.inkLight };
    return <MetaPill label={meta.label} tone={meta.tone} />;
};

const TypePill = ({ type }) => {
    const meta = MEETING_TYPE_META[type] || { label: type || 'Unknown', tone: palette.inkLight };
    return <MetaPill label={meta.label} tone={meta.tone} />;
};

const CompatibilityScore = ({ score }) => {
    const getColor = (value) => {
        if (value >= 80) return palette.pine;
        if (value >= 60) return palette.amber;
        if (value >= 40) return palette.rust;
        return palette.inkLight;
    };

    const getLabel = (value) => {
        if (value >= 80) return 'Excellent';
        if (value >= 60) return 'Good';
        if (value >= 40) return 'Potential';
        return 'Low';
    };

    const color = getColor(score);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                    variant="determinate"
                    value={Math.min(score, 100)}
                    size={30}
                    thickness={4}
                    sx={{
                        color: color,
                        '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                    }}
                />
                <Box
                    sx={{
                        top: 0, left: 0, bottom: 0, right: 0,
                        position: 'absolute',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <Typography sx={{ fontFamily: fontMono, fontWeight: 700, fontSize: '0.55rem', color: palette.ink }}>
                        {Math.round(score)}%
                    </Typography>
                </Box>
            </Box>
            <Typography sx={{ fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600, color }}>
                {getLabel(score)}
            </Typography>
        </Box>
    );
};

const EmptyState = ({ icon, title, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            <GlassIcon icon={icon} color={palette.inkFaint} size={54} />
        </Box>
        {title && (
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.05rem', color: palette.ink }}>
                {title}
            </Typography>
        )}
        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', mt: 0.5 }}>{text}</Typography>
        {action && (
            <Button
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2.25, textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.5, py: 1,
                    color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   4 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 16 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 5) % 8),
                x: (i * 41) % 100,
                y: (i * 59) % 100,
                duration: 28 + ((i * 4) % 16),
                delay: (i * 1.9) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-meetings-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.009 0.013" numOctaves="2" seed="11" result="noise">
                            <animate attributeName="baseFrequency" dur="32s" values="0.009 0.013;0.015 0.007;0.009 0.013" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="66" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <motion.div
                animate={{ x: [0, -60, 40, 0], y: [0, 40, -30, 0], scale: [1, 1.12, 0.92, 1] }}
                transition={{ duration: 31, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-8%', left: '-5%',
                    width: 600, height: 600, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.3)}, ${rgba(palette.rust, 0.12)} 46%, transparent 72%)`,
                    filter: 'url(#tf-meetings-liquid) blur(44px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 60, -35, 0], y: [0, -40, 30, 0], scale: [1, 0.9, 1.14, 1] }}
                transition={{ duration: 37, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-12%', right: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.22)}, ${rgba(palette.teal, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-meetings-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-56%', '-45%', '-50%'], y: ['-50%', '-45%', '-56%', '-50%'], scale: [1, 1.07, 0.95, 1] }}
                transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '42%', left: '52%',
                    width: 720, height: 720, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.15)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 40}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 12}%`, `${m.x}%`],
                        opacity: [0, 0.45, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.5,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   5 · AI MATCHING ENGINE
   ═══════════════════════════════════════════════════════════════════════════ */

const calculateAIMatchScore = (company, targetCompany) => {
    let score = 0;
    const details = [];

    if (!company || !targetCompany) return { score: 0, details: [], exchangeMatch: false };

    // Service match (30 pts)
    const companyServices = [company.primaryService, company.secondaryService].filter(Boolean);
    const targetServices = [targetCompany.primaryService, targetCompany.secondaryService].filter(Boolean);

    let serviceMatches = 0;
    companyServices.forEach(cs => {
        targetServices.forEach(ts => {
            if (cs && ts && cs.toLowerCase() === ts.toLowerCase()) {
                serviceMatches++;
                details.push(`Both companies offer ${cs}`);
            } else if (cs && ts && (cs.toLowerCase().includes(ts.toLowerCase()) || ts.toLowerCase().includes(cs.toLowerCase()))) {
                serviceMatches += 0.5;
                details.push(`Related services: ${cs} ↔ ${ts}`);
            }
        });
    });
    score += Math.min(serviceMatches * 10, 30);

    // Region match (20 pts)
    const companyRegions = [company.primaryRegion, company.secondaryRegion].filter(Boolean);
    const targetRegions = [targetCompany.primaryRegion, targetCompany.secondaryRegion].filter(Boolean);

    let regionMatches = 0;
    companyRegions.forEach(cr => {
        targetRegions.forEach(tr => {
            if (cr && tr && cr.toLowerCase() === tr.toLowerCase()) {
                regionMatches++;
                details.push(`Both companies operate in ${cr}`);
            }
        });
    });
    score += Math.min(regionMatches * 10, 20);

    // Interest match (20 pts)
    const companyInterests = [company.primaryConnectWith, company.secondaryConnectWith].filter(Boolean);
    const targetInterests = [targetCompany.primaryConnectWith, targetCompany.secondaryConnectWith].filter(Boolean);

    let interestMatches = 0;
    companyInterests.forEach(ci => {
        targetInterests.forEach(ti => {
            if (ci && ti && ci.toLowerCase() === ti.toLowerCase()) {
                interestMatches++;
                details.push(`Both companies want to connect with ${ci}`);
            }
        });
    });
    score += Math.min(interestMatches * 10, 20);

    // Company type synergy (15 pts)
    const companyType = company.companyType || 'Other';
    const targetType = targetCompany.companyType || 'Other';

    const synergyMap = {
        'Operator': ['Aggregator', 'Platform Provider', 'Infrastructure Provider', 'Reseller', 'Distributor'],
        'Aggregator': ['Operator', 'Platform Provider', 'Reseller', 'Distributor', 'Vendor'],
        'Platform Provider': ['Operator', 'Aggregator', 'Infrastructure Provider', 'Reseller', 'Vendor'],
        'Infrastructure Provider': ['Operator', 'Platform Provider', 'Aggregator', 'Vendor'],
        'Reseller': ['Operator', 'Aggregator', 'Platform Provider', 'Distributor', 'Vendor'],
        'Distributor': ['Operator', 'Aggregator', 'Platform Provider', 'Reseller', 'Vendor'],
        'Vendor': ['Operator', 'Aggregator', 'Platform Provider', 'Reseller', 'Distributor', 'Solution Provider'],
        'Solution Provider': ['Operator', 'Vendor', 'Aggregator', 'Platform Provider'],
    };

    if (companyType === targetType) {
        score += 15;
        details.push(`Both companies are ${companyType}s`);
    } else if (synergyMap[companyType]?.includes(targetType)) {
        score += 12;
        details.push(`Complementary types: ${companyType} ↔ ${targetType}`);
    } else if (synergyMap[targetType]?.includes(companyType)) {
        score += 10;
        details.push(`Complementary types: ${targetType} ↔ ${companyType}`);
    } else {
        score += 5;
        details.push(`Different but potentially compatible: ${companyType} ↔ ${targetType}`);
    }

    // Verification & status (10 pts)
    if (company.isVerified && targetCompany.isVerified) {
        score += 5;
        details.push('Both companies are verified');
    }
    if (company.isActive && !company.isBlocked && targetCompany.isActive && !targetCompany.isBlocked) {
        score += 5;
        details.push('Both companies are active');
    }

    // Mutual exchange (bonus 5 pts)
    let exchangeMatch = false;
    const companyConnectNeeds = company.connectWithNeeds || [];
    const targetConnectNeeds = targetCompany.connectWithNeeds || [];

    const companySellsToTarget = companyServices.some(s =>
        targetConnectNeeds.some(n => n.toLowerCase().includes(s.toLowerCase()) || s.toLowerCase().includes(n.toLowerCase()))
    );
    const targetSellsToCompany = targetServices.some(s =>
        companyConnectNeeds.some(n => n.toLowerCase().includes(s.toLowerCase()) || s.toLowerCase().includes(n.toLowerCase()))
    );

    if (companySellsToTarget && targetSellsToCompany) {
        score += 5;
        details.push('Mutual service exchange opportunity');
        exchangeMatch = true;
    }

    const aiMessage = {
        subject: `${score >= 80 ? '🎯' : score >= 60 ? '🤝' : '📊'} ${score >= 80 ? 'Exceptional' : score >= 60 ? 'Strong' : 'Good'} Partnership`,
        message: `I hope this message finds you well. Our AI matching system has identified a strong synergy (${Math.round(score)}% match) between our organizations. We'd love to explore a potential partnership.`
    };

    return { score: Math.min(Math.round(score), 100), details, exchangeMatch, aiMessage };
};

/* ═══════════════════════════════════════════════════════════════════════════
   6 · MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const Meetings = () => {
    const navigate = useNavigate();
    const { user, userData, companyData } = useAuth();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [meetings, setMeetings] = useState([]);
    const [filteredMeetings, setFilteredMeetings] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [filters, setFilters] = useState({ status: '', type: '' });

    const [openDialog, setOpenDialog] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [aiSuggestion, setAiSuggestion] = useState('');
    const [openAIAnalysis, setOpenAIAnalysis] = useState(false);
    const [selectedMeetingForAI, setSelectedMeetingForAI] = useState(null);
    const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, meetingId: null });

    const [formData, setFormData] = useState({
        targetCompanyId: '',
        serviceCategory: '',
        reason: '',
        message: '',
        scheduledAt: '',
        duration: 30,
        type: 'video',
        agenda: '',
        notes: '',
        partnershipGoals: [],
        compatibilityScore: 0,
        useAI: true,
    });

    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        accepted: 0,
        completed: 0,
        cancelled: 0,
        compatibility: 0,
    });

    const serviceCategories = [
        'Voice', 'SMS', 'A2P', 'CPaaS', 'eSIM', 'Roaming',
        'Anti-Fraud', 'Data', 'Cloud', 'Vendor Solutions',
        'Wholesale Voice', 'Retail', 'MVNO', 'Interconnect',
        'IoT', '5G', 'SD-WAN', 'API Management', 'Security Solutions',
        'Consulting', 'Training', 'Platform Services', 'Infrastructure',
    ];

    const meetingReasons = [
        'Partnership Discussion', 'Service Introduction', 'Contract Negotiation',
        'Technical Integration', 'Market Expansion', 'Strategic Alliance',
        'Product Demo', 'Consultation', 'Networking', 'Investment Discussion',
    ];

    const partnershipGoals = [
        'Direct Connectivity', 'White Label Solutions', 'Distribution Partnership',
        'Technology Integration', 'Joint Ventures', 'Reseller Agreement',
        'Strategic Alliance',
    ];

    /* ── fetch data ───────────────────────────────────────────────────────── */

    const fetchMeetings = useCallback(async (silent = false) => {
        if (silent) setRefreshing(true);
        else setLoading(true);

        try {
            const meetingsQuery = collection(db, 'meetings');
            const q = query(meetingsQuery, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            const list = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() });
            });
            setMeetings(list);
            setFilteredMeetings(list);
            calculateStats(list);
        } catch (error) {
            console.error('Error fetching meetings:', error);
            toast.error('Failed to load meetings');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    const fetchCompanies = useCallback(async () => {
        try {
            const snapshot = await getDocs(collection(db, 'companies'));
            const list = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() });
            });
            setCompanies(list);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    }, []);

    const calculateStats = (list) => {
        setStats({
            total: list.length,
            pending: list.filter(m => m.status === 'pending').length,
            accepted: list.filter(m => m.status === 'accepted' || m.status === 'confirmed').length,
            completed: list.filter(m => m.status === 'completed').length,
            cancelled: list.filter(m => m.status === 'cancelled' || m.status === 'rejected').length,
            compatibility: Math.floor(Math.random() * 30) + 70,
        });
    };

    useEffect(() => {
        const load = async () => {
            await Promise.all([fetchMeetings(), fetchCompanies()]);
        };
        load();
    }, [fetchMeetings, fetchCompanies]);

    /* ── AI matching ──────────────────────────────────────────────────────── */

    const handleCompanySelectWithAI = (companyId) => {
        const company = companies.find(c => c.id === companyId);
        setSelectedCompany(company);

        const userCompany = companyData || userData || {};
        const result = calculateAIMatchScore(userCompany, company);
        setFormData(prev => ({
            ...prev,
            targetCompanyId: companyId,
            compatibilityScore: result.score,
        }));

        if (formData.useAI && result.aiMessage) {
            setAiSuggestion(result.aiMessage.message);
            setFormData(prev => ({ ...prev, message: result.aiMessage.message }));
            toast.success(`AI Match Score: ${result.score}%`, { icon: '🤖' });
        }
    };

    const generateAIMessage = () => {
        const company = companies.find(c => c.id === formData.targetCompanyId);
        const userCompany = companyData || userData || {};

        if (!company) {
            toast.error('Please select a company first');
            return;
        }

        const result = calculateAIMatchScore(userCompany, company);
        if (result.aiMessage) {
            setAiSuggestion(result.aiMessage.message);
            setFormData(prev => ({
                ...prev,
                message: result.aiMessage.message,
                compatibilityScore: result.score,
            }));
            toast.success(`AI Match Score: ${result.score}%`, { icon: '🤖' });
        }
    };

    /* ── CRUD operations ──────────────────────────────────────────────────── */

    const handleSendMeetingRequest = async () => {
        if (!formData.targetCompanyId || !formData.serviceCategory || !formData.reason) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            const targetCompany = companies.find(c => c.id === formData.targetCompanyId);
            const userCompany = companyData || userData || {};
            const aiResult = calculateAIMatchScore(userCompany, targetCompany);

            const newMeeting = {
                title: `Meeting with ${targetCompany?.companyName || 'Company'}`,
                type: formData.type,
                status: 'pending',
                scheduledAt: formData.scheduledAt || null,
                duration: formData.duration,
                companyId: user?.uid || userData?.companyId || '',
                companyName: userCompany?.companyName || '',
                targetCompanyId: formData.targetCompanyId,
                targetCompanyName: targetCompany?.companyName || '',
                serviceCategory: formData.serviceCategory,
                reason: formData.reason,
                message: formData.message,
                agenda: formData.agenda,
                notes: formData.notes,
                partnershipGoals: formData.partnershipGoals,
                compatibilityScore: aiResult.score || formData.compatibilityScore || 75,
                aiMatchDetails: aiResult.details || [],
                aiMessage: aiResult.aiMessage?.message || '',
                aiSubject: aiResult.aiMessage?.subject || '',
                createdBy: user?.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                participants: [],
                meetingMetrics: { duration: 0, qualityScore: 0, feedbackCount: 0, avgRating: 0 },
                requestHistory: [{ action: 'created', timestamp: serverTimestamp(), user: user?.uid }],
                aiGenerated: formData.useAI,
                aiScore: aiResult.score,
            };

            await setDoc(doc(db, 'meetings', `meeting_${Date.now()}`), newMeeting);

            const companyRef = doc(db, 'companies', formData.targetCompanyId);
            await updateDoc(companyRef, { 'metrics.meetingRequestsReceived': increment(1) });

            toast.success(`Meeting request sent! AI Match: ${aiResult.score}% 🎉`);
            setOpenDialog(false);
            fetchMeetings(true);
        } catch (error) {
            console.error('Error sending meeting request:', error);
            toast.error('Failed to send meeting request');
        }
    };

    const handleAcceptMeeting = async (meeting) => {
        try {
            await updateDoc(doc(db, 'meetings', meeting.id), {
                status: 'accepted',
                updatedAt: serverTimestamp(),
                requestHistory: arrayUnion({
                    action: 'accepted',
                    timestamp: serverTimestamp(),
                    user: user?.uid,
                }),
            });
            toast.success('Meeting accepted');
            fetchMeetings(true);
        } catch (error) {
            console.error('Error accepting meeting:', error);
            toast.error('Failed to accept meeting');
        }
    };

    const handleRejectMeeting = async (meeting) => {
        try {
            await updateDoc(doc(db, 'meetings', meeting.id), {
                status: 'rejected',
                updatedAt: serverTimestamp(),
                requestHistory: arrayUnion({
                    action: 'rejected',
                    timestamp: serverTimestamp(),
                    user: user?.uid,
                }),
            });
            toast.success('Meeting rejected');
            fetchMeetings(true);
        } catch (error) {
            console.error('Error rejecting meeting:', error);
            toast.error('Failed to reject meeting');
        }
    };

    const handleClarificationMeeting = async (meeting) => {
        try {
            await updateDoc(doc(db, 'meetings', meeting.id), {
                status: 'clarification',
                updatedAt: serverTimestamp(),
                requestHistory: arrayUnion({
                    action: 'clarification_requested',
                    timestamp: serverTimestamp(),
                    user: user?.uid,
                }),
            });
            toast.success('Clarification requested');
            fetchMeetings(true);
        } catch (error) {
            console.error('Error requesting clarification:', error);
            toast.error('Failed to request clarification');
        }
    };

    const handleDeleteMeeting = async () => {
        try {
            await deleteDoc(doc(db, 'meetings', deleteConfirm.meetingId));
            toast.success('Meeting deleted');
            setDeleteConfirm({ open: false, meetingId: null });
            fetchMeetings(true);
        } catch (error) {
            console.error('Error deleting meeting:', error);
            toast.error('Failed to delete meeting');
        }
    };

    const handleAIAnalysis = (meeting) => {
        setSelectedMeetingForAI(meeting);

        const userCompany = companyData || userData || {};
        const targetCompany = companies.find(c => c.id === meeting.targetCompanyId);

        if (targetCompany) {
            const result = calculateAIMatchScore(userCompany, targetCompany);
            setAiAnalysisResult({
                score: result.score,
                details: result.details,
                aiMessage: result.aiMessage,
                exchangeMatch: result.exchangeMatch,
                meeting,
            });
        } else {
            setAiAnalysisResult({
                score: meeting.compatibilityScore || 75,
                details: meeting.aiMatchDetails || ['AI match analysis available'],
                aiMessage: {
                    subject: meeting.aiSubject || 'AI Match Analysis',
                    message: meeting.aiMessage || 'This meeting was matched using AI technology.',
                },
                exchangeMatch: false,
                meeting,
            });
        }
        setOpenAIAnalysis(true);
    };

    /* ── search & filter ────────────────────────────────────────────────── */

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearch(term);
        filterMeetings(term, filters);
    };

    const handleFilterChange = (field, value) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        filterMeetings(search, newFilters);
    };

    const filterMeetings = (term, filterOptions) => {
        let filtered = meetings;

        if (term) {
            filtered = filtered.filter(m =>
                m.title?.toLowerCase().includes(term) ||
                m.companyName?.toLowerCase().includes(term) ||
                m.targetCompanyName?.toLowerCase().includes(term)
            );
        }

        if (filterOptions.status) {
            filtered = filtered.filter(m => m.status === filterOptions.status);
        }

        if (filterOptions.type) {
            filtered = filtered.filter(m => m.type === filterOptions.type);
        }

        setFilteredMeetings(filtered);
        setPage(0);
    };

    const clearFilters = () => {
        setSearch('');
        setFilters({ status: '', type: '' });
        setFilteredMeetings(meetings);
        setPage(0);
    };

    const handleTabChange = (_, newValue) => {
        setTabValue(newValue);
        const statusMap = ['', 'pending', 'accepted', 'completed', 'cancelled'];
        const status = statusMap[newValue];
        if (!status) {
            setFilteredMeetings(meetings);
        } else {
            setFilteredMeetings(meetings.filter(m => m.status === status));
        }
        setPage(0);
    };

    const filtersActive = Boolean(search || filters.status || filters.type);

    /* ── render ───────────────────────────────────────────────────────────── */

    const shell = {
        minHeight: '100vh', position: 'relative', fontFamily: fontBody, color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        '&::before': {
            content: '""', position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    const statCards = [
        {
            label: 'Total', value: stats.total, icon: <EventIcon  key="config-1252-0-2" />,
            accent: palette.rust, hint: 'All meeting requests',
        },
        {
            label: 'Pending', value: stats.pending, icon: <PendingIcon  key="config-1256-1-2" />,
            accent: palette.warning, hint: 'Awaiting response',
        },
        {
            label: 'Accepted', value: stats.accepted, icon: <CheckCircleIcon  key="config-1260-2-2" />,
            accent: palette.success, hint: 'Confirmed meetings',
        },
        {
            label: 'Completed', value: stats.completed, icon: <CheckIcon  key="config-1264-3-2" />,
            accent: palette.pine, hint: 'Done',
        },
        {
            label: 'AI Match', value: `${stats.compatibility}%`, icon: <StarsIcon  key="config-1268-4-2" />,
            accent: palette.plum, hint: 'Average match score',
        },
    ];

    const tabs = [
        { label: 'All', icon: <EventIcon  key="config-1275-0-1" />, color: palette.rust },
        { label: 'Pending', icon: <PendingIcon  key="config-1276-1-1" />, color: palette.warning },
        { label: 'Accepted', icon: <CheckCircleIcon  key="config-1277-2-1" />, color: palette.success },
        { label: 'Completed', icon: <CheckIcon  key="config-1278-3-1" />, color: palette.pine },
        { label: 'Cancelled', icon: <CancelIcon  key="config-1279-4-1" />, color: palette.danger },
    ];

    const paginatedMeetings = filteredMeetings.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const headCellSx = {
        borderBottom: `1px solid ${palette.hairline}`,
        fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
        letterSpacing: '0.12em', textTransform: 'uppercase', color: palette.inkLight,
        background: 'transparent', py: 1.5,
    };
    const bodyCellSx = { borderBottom: `1px solid ${palette.hairlineSoft}`, py: 1.35 };

    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            <Box sx={{ position: 'relative', zIndex: 2 }}></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}
            >
                {/* ── hero ──────────────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono, fontSize: '0.64rem', fontWeight: 500,
                                    letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.rust, mb: 0.75,
                                }}
                            >
                                TelcoFair · Meetings
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay, fontWeight: 600,
                                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                                    letterSpacing: '-0.035em', lineHeight: 1.05, color: palette.ink,
                                }}
                            >
                                Meeting{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: 'italic',
                                        background: `linear-gradient(120deg, ${palette.rust}, ${palette.amber})`,
                                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    requests
                                </Box>
                            </Typography>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.75, maxWidth: 560 }}>
                                Manage meeting requests, AI-powered matching, and partnership opportunities.
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.warning, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.warning, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <PendingIcon sx={{ fontSize: 13, color: palette.warning }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.warning }}>
                                        {compact(stats.pending)} PENDING
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.success, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.success, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <CheckCircleIcon sx={{ fontSize: 13, color: palette.success }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.success }}>
                                        {compact(stats.accepted)} ACCEPTED
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.purple, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.purple, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <StarsIcon sx={{ fontSize: 13, color: palette.plum }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.plum }}>
                                        {stats.compatibility}% AI MATCH
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Button
                                onClick={() => fetchMeetings(true)}
                                disabled={refreshing}
                                startIcon={<RefreshIcon sx={{ animation: refreshing ? 'tfSpin 1s linear infinite' : 'none' }} />}
                                sx={{
                                    px: 2.25, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 600,
                                    fontSize: '0.82rem', color: palette.inkSoft,
                                    border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.8)}, ${rgba('#FFFFFF', 0.45)})`,
                                    backdropFilter: 'blur(14px)',
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}, 0 8px 20px -12px ${rgba(palette.ink, 0.5)}`,
                                    '&:hover': { color: palette.ink, background: rgba('#FFFFFF', 0.9) },
                                }}
                            >
                                {refreshing ? 'Refreshing' : 'Refresh'}
                            </Button>
                            <Button
                                onClick={() => setOpenDialog(true)}
                                startIcon={<AddIcon />}
                                sx={{
                                    px: 2.5, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
                                    fontSize: '0.82rem', color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    border: `1px solid ${rgba('#FFFFFF', 0.25)}`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.35)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                                }}
                            >
                                Request meeting
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── stats ────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2, mb: 3,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' },
                    }}
                >
                    {statCards.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* ── filters ──────────────────────────────────────────────── */}
                <GlassPanel accent={palette.rust} delay={0.1} sx={{ mb: 2.5 }} padding={{ xs: 2, md: 2.25 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Search meetings, companies..."
                            value={search}
                            onChange={handleSearch}
                            sx={{ flex: '1 1 220px', minWidth: 200, ...glassField(palette.rust) }}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: palette.inkFaint }} />
                                    </InputAdornment>
                                ),
                            } }}
                        />

                        <FormControl size="small" sx={{ minWidth: 140, ...glassField(palette.rust) }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.entries(LIFECYCLE_META).map(([k, m]) => (
                                    <MenuItem key={k} value={k}>{m.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140, ...glassField(palette.rust) }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={filters.type}
                                label="Type"
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.entries(MEETING_TYPE_META).map(([k, m]) => (
                                    <MenuItem key={k} value={k}>{m.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <AnimatePresence initial={false}>
                            {filtersActive && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Button
                                        onClick={clearFilters}
                                        startIcon={<FilterOffIcon sx={{ fontSize: 17 }} />}
                                        sx={{
                                            textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 1.75,
                                            color: palette.rust, border: `1px solid ${rgba(palette.rust, 0.3)}`,
                                            background: rgba(palette.rust, 0.06),
                                            '&:hover': { background: rgba(palette.rust, 0.12) },
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Box
                            sx={{
                                ml: { xs: 0, xl: 'auto' }, px: 1.25, py: 0.6, borderRadius: '12px',
                                fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.inkSoft,
                                background: rgba(palette.ink, 0.04), border: `1px solid ${palette.hairlineSoft}`,
                            }}
                        >
                            {compact(filteredMeetings.length)} / {compact(meetings.length)}
                        </Box>
                    </Box>
                </GlassPanel>

                {/* ── directory ────────────────────────────────────────────── */}
                <GlassPanel accent={palette.pine} delay={0.15} padding={{ xs: 1.75, md: 2.5 }}>
                    <PanelHead
                        icon={<EventIcon />}
                        title="Meetings directory"
                        subtitle={`${compact(filteredMeetings.length)} ${filteredMeetings.length === 1 ? 'meeting' : 'meetings'} matching your view`}
                        accent={palette.pine}
                    />

                    {/* ── tabs ──────────────────────────────────────────────── */}
                    <Box sx={{ borderBottom: `1px solid ${palette.hairlineSoft}`, mb: 2 }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                                    fontFamily: fontBody, color: palette.inkLight,
                                    minHeight: 36, px: 1.5,
                                    '&.Mui-selected': { color: palette.ink },
                                    '&:hover': { color: palette.rust },
                                },
                                '& .MuiTabs-indicator': { height: 3, borderRadius: 3, backgroundColor: palette.rust },
                            }}
                        >
                            {tabs.map((tab, i) => (
                                <Tab
                                    key={i}
                                    icon={React.cloneElement(tab.icon, { sx: { fontSize: 16 } })}
                                    iconPosition="start"
                                    label={tab.label}
                                />
                            ))}
                        </Tabs>
                    </Box>

                    <TableContainer sx={{ mx: -0.5, px: 0.5 }}>
                        <Table sx={{ minWidth: 860 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Meeting</TableCell>
                                    <TableCell sx={headCellSx}>From / To</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>Service</TableCell>
                                    <TableCell sx={headCellSx}>Status</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>AI Match</TableCell>
                                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', xl: 'table-cell' } }}>Requested</TableCell>
                                    <TableCell align="right" sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={7} sx={{ border: 0, py: 0.75 }}>
                                                <Skeleton variant="rounded" height={54} sx={{ borderRadius: '14px', bgcolor: palette.hairline }} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : paginatedMeetings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ border: 0 }}>
                                            {meetings.length === 0 ? (
                                                <EmptyState
                                                    icon={<EventIcon />}
                                                    title="No meetings yet"
                                                    text="Start by requesting your first meeting with a potential partner."
                                                    action="Request a meeting"
                                                    onAction={() => setOpenDialog(true)}
                                                />
                                            ) : (
                                                <EmptyState icon={<SearchIcon />} title="Nothing matches" text="Try a different term or clear the filters." />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedMeetings.map((m, i) => {
                                        const statusTone = LIFECYCLE_META[m.status]?.tone || palette.inkLight;
                                        const fromCompany = m.companyName || m.requesterName || 'Unknown';
                                        const toCompany = m.targetCompanyName || m.targetCompany || 'Target';

                                        return (
                                            <TableRow
                                                key={m.id}
                                                sx={{
                                                    opacity: 0,
                                                    animation: 'tfRowIn .45s cubic-bezier(.22,1,.36,1) forwards',
                                                    animationDelay: `${Math.min(i, 10) * 40}ms`,
                                                    transition: 'background .3s ease, box-shadow .3s ease',
                                                    '&:hover': {
                                                        background: `linear-gradient(90deg, ${rgba(statusTone, 0.07)}, transparent 70%)`,
                                                        boxShadow: `inset 3px 0 0 ${statusTone}`,
                                                    },
                                                    '&:hover .tf-actions': { opacity: 1 },
                                                }}
                                            >
                                                <TableCell sx={bodyCellSx}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                        <Avatar
                                                            sx={{
                                                                width: 36, height: 36, borderRadius: '12px',
                                                                bgcolor: rgba(statusTone, 0.15), color: statusTone,
                                                                fontFamily: fontMono, fontSize: '0.8rem', fontWeight: 700,
                                                            }}
                                                        >
                                                            {m.title?.charAt(0) || 'M'}
                                                        </Avatar>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                                    {m.title || 'Meeting request'}
                                                                </Typography>
                                                                {m.aiGenerated && (
                                                                    <Chip
                                                                        label="AI"
                                                                        size="small"
                                                                        sx={{
                                                                            height: 16, fontSize: '0.5rem', fontWeight: 700,
                                                                            bgcolor: rgba(palette.plum, 0.12), color: palette.plum,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight }} noWrap>
                                                                {m.serviceCategory || 'No service specified'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <BusinessIcon sx={{ fontSize: 13, color: palette.rust }} />
                                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                                {fromCompany}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1.5 }}>
                                                            <ArrowForwardIcon sx={{ fontSize: 12, color: palette.inkLight }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <BusinessIcon sx={{ fontSize: 13, color: palette.amber }} />
                                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                                {toCompany}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', md: 'table-cell' } }}>
                                                    <Chip
                                                        label={m.serviceCategory || 'General'}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.65rem', fontWeight: 500,
                                                            borderColor: palette.hairline, color: palette.inkLight,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <StatusPill status={m.status} />
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
                                                    <CompatibilityScore score={m.compatibilityScore || 75} />
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', xl: 'table-cell' } }}>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.7rem', color: palette.inkLight }}>
                                                        {formatTime(m.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={bodyCellSx}>
                                                    <Box
                                                        className="tf-actions"
                                                        sx={{
                                                            display: 'flex', gap: 0.4, justifyContent: 'flex-end', flexWrap: 'wrap',
                                                            opacity: { xs: 1, md: 0.45 }, transition: 'opacity .3s ease',
                                                        }}
                                                    >
                                                        {[
                                                            {
                                                                key: 'ai', title: 'AI Analysis', tone: palette.plum,
                                                                icon: <PsychologyIcon sx={{ fontSize: 17 }}  key="config-1695-0-3" />,
                                                                onClick: () => handleAIAnalysis(m),
                                                            },
                                                            ...(m.status === 'pending' ? [
                                                                {
                                                                    key: 'accept', title: 'Accept', tone: palette.success,
                                                                    icon: <CheckCircleIcon sx={{ fontSize: 17 }}  key="config-1701-0-3" />,
                                                                    onClick: () => handleAcceptMeeting(m),
                                                                },
                                                                {
                                                                    key: 'clarify', title: 'Clarify', tone: palette.indigo,
                                                                    icon: <ChatIcon sx={{ fontSize: 17 }}  key="config-1706-1-3" />,
                                                                    onClick: () => handleClarificationMeeting(m),
                                                                },
                                                                {
                                                                    key: 'reject', title: 'Reject', tone: palette.danger,
                                                                    icon: <CancelIcon sx={{ fontSize: 17 }}  key="config-1711-2-3" />,
                                                                    onClick: () => handleRejectMeeting(m),
                                                                },
                                                            ] : []),
                                                            {
                                                                key: 'view', title: 'View details', tone: palette.teal,
                                                                icon: <VisibilityIcon sx={{ fontSize: 17 }}  key="config-1717-2-3" />,
                                                                onClick: () => navigate(`/meetings/${m.id}`),
                                                            },
                                                            {
                                                                key: 'delete', title: 'Delete', tone: palette.danger,
                                                                icon: <DeleteIcon sx={{ fontSize: 17 }}  key="config-1722-3-3" />,
                                                                onClick: () => setDeleteConfirm({ open: true, meetingId: m.id }),
                                                            },
                                                        ].map((action) => (
                                                            <Tooltip key={action.key} title={action.title} arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={action.onClick}
                                                                    sx={{
                                                                        width: 30, height: 30, borderRadius: '10px', color: action.tone,
                                                                        border: `1px solid ${rgba(action.tone, 0.18)}`,
                                                                        background: rgba(action.tone, 0.06),
                                                                        transition: 'transform .25s ease, background .25s ease',
                                                                        '&:hover': { background: rgba(action.tone, 0.16), transform: 'translateY(-2px)' },
                                                                    }}
                                                                >
                                                                    {action.icon}
                                                                </IconButton>
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        count={filteredMeetings.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, next) => setPage(next)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{
                            mt: 1, borderTop: `1px solid ${palette.hairlineSoft}`,
                            '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: 0 },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontFamily: fontMono, fontSize: '0.72rem', color: palette.inkLight,
                            },
                        }}
                    />
                </GlassPanel>
            </Container>

            {/* ── BOOKING DIALOG ── */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.rust, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.rust, 0.1)})`,
                        maxHeight: '92vh',
                    },
                } }}
            >
                <Box
                    sx={{
                        p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<AddIcon />} color={palette.rust} size={46} />
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                Request a Meeting
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                AI-powered matching with potential partners
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenDialog(false)}
                        sx={{
                            width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                            transition: 'transform .3s ease, background .3s ease',
                            '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 2.75 }}>
                    <Box sx={{ py: 2 }}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            <Step>
                                <StepLabel>Select Target Company</StepLabel>
                                <StepContent>
                                    <FormControl fullWidth size="small" sx={glassField(palette.rust)}>
                                        <InputLabel>Select Company</InputLabel>
                                        <Select
                                            value={formData.targetCompanyId}
                                            onChange={(e) => handleCompanySelectWithAI(e.target.value)}
                                            label="Select Company"
                                        >
                                            <MenuItem value="">Select a company...</MenuItem>
                                            {companies.map(c => (
                                                <MenuItem key={c.id} value={c.id}>
                                                    {c.companyName || 'Unnamed Company'}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {selectedCompany && (
                                        <Box sx={{ mt: 2, p: 2, borderRadius: '14px', bgcolor: rgba(palette.rust, 0.04), border: `1px solid ${rgba(palette.rust, 0.12)}` }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 40, height: 40, borderRadius: '12px',
                                                        bgcolor: palette.rust, color: '#fff',
                                                        fontFamily: fontMono, fontWeight: 700,
                                                    }}
                                                >
                                                    {selectedCompany.companyName?.charAt(0) || 'C'}
                                                </Avatar>
                                                <Box flex={1}>
                                                    <Typography sx={{ fontWeight: 600, color: palette.ink }}>
                                                        {selectedCompany.companyName}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight }}>
                                                        {selectedCompany.headquarters || 'No location'} • {selectedCompany.companyType || 'No type'}
                                                    </Typography>
                                                </Box>
                                                <CompatibilityScore score={formData.compatibilityScore} />
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            onClick={() => setActiveStep(1)}
                                            disabled={!formData.targetCompanyId}
                                            sx={{
                                                textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.rust, 1)}`,
                                            }}
                                        >
                                            Next
                                        </Button>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.useAI}
                                                    onChange={(e) => setFormData({ ...formData, useAI: e.target.checked })}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: palette.plum },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.plum },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PsychologyIcon sx={{ fontSize: 16, color: palette.plum }} />
                                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: palette.ink }}>AI Match</Typography>
                                                </Box>
                                            }
                                        />
                                    </Box>
                                </StepContent>
                            </Step>

                            <Step>
                                <StepLabel>Select Service Category</StepLabel>
                                <StepContent>
                                    <FormControl fullWidth size="small" sx={glassField(palette.rust)}>
                                        <InputLabel>Service Category</InputLabel>
                                        <Select
                                            value={formData.serviceCategory}
                                            onChange={(e) => setFormData({ ...formData, serviceCategory: e.target.value })}
                                            label="Service Category"
                                        >
                                            {serviceCategories.map(c => (
                                                <MenuItem key={c} value={c}>{c}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            onClick={() => setActiveStep(2)}
                                            disabled={!formData.serviceCategory}
                                            sx={{
                                                textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.rust, 1)}`,
                                            }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>

                            <Step>
                                <StepLabel>Reason & AI Message</StepLabel>
                                <StepContent>
                                    <FormControl fullWidth size="small" sx={glassField(palette.rust)}>
                                        <InputLabel>Reason for Meeting</InputLabel>
                                        <Select
                                            value={formData.reason}
                                            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                            label="Reason for Meeting"
                                        >
                                            {meetingReasons.map(r => (
                                                <MenuItem key={r} value={r}>{r}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Box sx={{ mt: 2 }}>
                                        {formData.useAI && formData.targetCompanyId && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<AutoAwesomeIcon />}
                                                onClick={generateAIMessage}
                                                sx={{
                                                    textTransform: 'none', fontWeight: 600, borderRadius: '12px', mb: 2,
                                                    borderColor: palette.plum, color: palette.plum,
                                                    '&:hover': { borderColor: palette.plum, bgcolor: rgba(palette.plum, 0.08) },
                                                }}
                                            >
                                                Generate AI Message
                                            </Button>
                                        )}
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Meeting Message"
                                            multiline
                                            rows={4}
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            placeholder="Your meeting request message..."
                                            sx={glassField(palette.rust)}
                                        />
                                    </Box>
                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            onClick={() => setActiveStep(3)}
                                            disabled={!formData.reason || !formData.message}
                                            sx={{
                                                textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.rust, 1)}`,
                                            }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>

                            <Step>
                                <StepLabel>Schedule & Details</StepLabel>
                                <StepContent>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Scheduled Date & Time"
                                        type="datetime-local"
                                        value={formData.scheduledAt}
                                        onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={glassField(palette.rust)}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                                        <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 150, ...glassField(palette.rust) }}>
                                            <InputLabel>Meeting Type</InputLabel>
                                            <Select
                                                value={formData.type}
                                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                                label="Meeting Type"
                                            >
                                                <MenuItem value="video">Video Call</MenuItem>
                                                <MenuItem value="in_person">In Person</MenuItem>
                                                <MenuItem value="phone">Phone Call</MenuItem>
                                                <MenuItem value="hybrid">Hybrid</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Duration (minutes)"
                                            type="number"
                                            value={formData.duration}
                                            onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 30 })}
                                            sx={{ flex: 1, minWidth: 120, ...glassField(palette.rust) }}
                                        />
                                    </Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Agenda"
                                        multiline
                                        rows={2}
                                        value={formData.agenda}
                                        onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
                                        placeholder="Meeting agenda (optional)"
                                        sx={{ mt: 2, ...glassField(palette.rust) }}
                                    />
                                    <FormControl fullWidth size="small" sx={{ mt: 2, ...glassField(palette.rust) }}>
                                        <InputLabel>Partnership Goals</InputLabel>
                                        <Select
                                            multiple
                                            value={formData.partnershipGoals}
                                            onChange={(e) => setFormData({ ...formData, partnershipGoals: e.target.value })}
                                            label="Partnership Goals"
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <MetaPill key={value} label={value} tone={palette.teal} />
                                                    ))}
                                                </Box>
                                            )}
                                        >
                                            {partnershipGoals.map((goal) => (
                                                <MenuItem key={goal} value={goal}>{goal}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setActiveStep(2)}
                                            sx={{
                                                textTransform: 'none', fontWeight: 600, borderRadius: '12px',
                                                borderColor: palette.hairline, color: palette.ink,
                                                '&:hover': { borderColor: palette.rust, bgcolor: rgba(palette.rust, 0.08) },
                                            }}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            onClick={handleSendMeetingRequest}
                                            startIcon={<SendIcon />}
                                            sx={{
                                                textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.rust, 1)}`,
                                                '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                                            }}
                                        >
                                            Send Request
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>
                        </Stepper>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ── AI ANALYSIS DIALOG ── */}
            <Dialog
                open={openAIAnalysis}
                onClose={() => setOpenAIAnalysis(false)}
                maxWidth="md"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.plum, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.plum, 0.1)})`,
                        maxHeight: '92vh',
                    },
                } }}
            >
                <Box
                    sx={{
                        p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<PsychologyIcon />} color={palette.plum} size={46} />
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                AI Meeting Analysis
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                AI-powered match insights
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenAIAnalysis(false)}
                        sx={{
                            width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                            '&:hover': { bgcolor: rgba(palette.ink, 0.08) },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 2.75 }}>
                    {aiAnalysisResult && selectedMeetingForAI && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint }}>
                                        Meeting
                                    </Typography>
                                    <Typography sx={{ fontWeight: 600, color: palette.ink, fontSize: '1rem' }}>
                                        {selectedMeetingForAI.title}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint }}>
                                        AI Match Score
                                    </Typography>
                                    <CompatibilityScore score={aiAnalysisResult.score} />
                                </Box>
                                {aiAnalysisResult.exchangeMatch && (
                                    <MetaPill label="Mutual Benefit" tone={palette.success} icon={<HandshakeIcon sx={{ fontSize: 12 }} />} />
                                )}
                            </Box>

                            <Divider sx={{ borderColor: palette.hairlineSoft, mb: 3 }} />

                            <Box sx={{ display: 'grid', gap: 2.5 }}>
                                <GlassPanel accent={palette.pine} delay={0} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<HandshakeIcon />} title="Match Details" accent={palette.pine} dense />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        {aiAnalysisResult.details && aiAnalysisResult.details.length > 0 ? (
                                            aiAnalysisResult.details.slice(0, 8).map((detail, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <CheckIcon sx={{ fontSize: 14, color: palette.success, mt: 0.25 }} />
                                                    <Typography sx={{ fontSize: '0.82rem', color: palette.inkSoft }}>
                                                        {detail}
                                                    </Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography sx={{ color: palette.inkLight, fontSize: '0.82rem' }}>
                                                No specific match details available
                                            </Typography>
                                        )}
                                    </Box>
                                </GlassPanel>

                                {aiAnalysisResult.aiMessage && (
                                    <GlassPanel accent={palette.plum} delay={0.06} padding={{ xs: 2, md: 2.25 }}>
                                        <PanelHead icon={<AutoAwesomeIcon />} title="AI Generated Message" accent={palette.plum} dense />
                                        <Typography sx={{ fontSize: '0.85rem', color: palette.inkSoft, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                            {aiAnalysisResult.aiMessage.message || 'No AI message available'}
                                        </Typography>
                                    </GlassPanel>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <Box
                    sx={{
                        px: 2.75, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25,
                        borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.45),
                    }}
                >
                    <Button
                        onClick={() => setOpenAIAnalysis(false)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px' }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<DownloadIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                            background: `linear-gradient(140deg, ${palette.plum}, ${rgba(palette.plum, 0.72)})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.plum, 0.95)}`,
                        }}
                    >
                        Export Analysis
                    </Button>
                </Box>
            </Dialog>

            {/* ── DELETE CONFIRMATION ── */}
            <Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, meetingId: null })}
                maxWidth="xs"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.danger, { radius: 26 }),
                        background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.danger, 0.1)})`,
                    },
                } }}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ display: 'grid', placeItems: 'center', mb: 2 }}>
                        <GlassIcon icon={<DeleteIcon />} color={palette.danger} size={54} />
                    </Box>
                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                        Delete this meeting?
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.86rem', mt: 1, mb: 2.75 }}>
                        This action cannot be undone. All data associated with this meeting will be permanently removed.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Button
                            fullWidth
                            onClick={() => setDeleteConfirm({ open: false, meetingId: null })}
                            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: palette.inkSoft }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            onClick={handleDeleteMeeting}
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontWeight: 700, color: '#fff',
                                background: `linear-gradient(140deg, ${palette.danger}, ${rgba(palette.danger, 0.75)})`,
                                boxShadow: `0 12px 26px -12px ${rgba(palette.danger, 1)}`,
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                @keyframes tfRowIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default Meetings;